import { Link } from 'gatsby';
import * as React from 'react';
import { createPortal } from 'react-dom';

import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import styles from './MobileMenu.module.css';

export interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  lang: 'de' | 'en';
  currentPath: string;
}

interface NavItem {
  label: string;
  path: string;
}

const NAV_ITEMS_DE: NavItem[] = [
  { label: 'Kanzlei', path: '/kanzlei/' },
  { label: 'Fachgebiete', path: '/fachgebiete/' },
  { label: 'Team', path: '/team/' },
  { label: 'Kontakt', path: '/kontakt/' },
];

const NAV_ITEMS_EN: NavItem[] = [
  { label: 'The Firm', path: '/en/the-firm/' },
  { label: 'Areas of Expertise', path: '/en/areas-of-expertise/' },
  { label: 'Team', path: '/en/team/' },
  { label: 'Contact', path: '/en/contact/' },
];

/**
 * MobileMenu – Slide-out mobile navigation with ARIA attributes.
 *
 * Features:
 * - Focus trap when open (Tab cycles within menu)
 * - Escape key dismissal
 * - Close on link selection
 * - Minimum 44×44px touch targets
 * - aria-expanded communicated via toggle button in NavigationBar
 */
const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, lang, currentPath }) => {
  const menuRef = React.useRef<HTMLDivElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const navItems = lang === 'de' ? NAV_ITEMS_DE : NAV_ITEMS_EN;
  const alternateSlug = lang === 'de' ? '/en/' : '/';

  // Focus the close button when menu opens
  React.useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  // Handle Escape key dismissal
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap: keep Tab cycling within the menu
  React.useEffect(() => {
    if (!isOpen || !menuRef.current) return;

    const handleFocusTrap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const menu = menuRef.current;
      if (!menu) return;

      const focusableElements = menu.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleFocusTrap);
    return () => document.removeEventListener('keydown', handleFocusTrap);
  }, [isOpen]);

  // Prevent body scroll when menu is open.
  // Plain `overflow: hidden` snaps the page to scrollTop 0 the instant it's
  // applied (there's no longer any overflow to have a scroll offset in), which
  // reads as the sticky header suddenly jumping. Freezing the body in place at
  // its current scroll offset instead avoids that jump and restores it on close.
  React.useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';

    return () => {
      // Un-freeze first — the body must be back in normal flow for the
      // document to have a scrollable range to restore into.
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      // 'instant' bypasses the global `scroll-behavior: smooth` (global.css)
      // — without it, the plain scrollTo(x, y) form animates the restore,
      // which reads as an unwanted scroll firing on close.
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    };
  }, [isOpen]);

  const isActive = (path: string): boolean => {
    return currentPath === path || currentPath.startsWith(path);
  };

  // Portal to <body> so the overlay/panel sit outside the sticky header's
  // stacking context — otherwise the header (a nested descendant) always
  // paints underneath them, making it look like it vanishes when open.
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className={`${styles.overlay} ${isOpen ? styles.overlayOpen : ''}`}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Menu panel */}
      <div
        ref={menuRef}
        id="mobile-menu"
        className={`${styles.menu} ${isOpen ? styles.menuOpen : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={lang === 'de' ? 'Mobilnavigation' : 'Mobile navigation'}
        aria-hidden={!isOpen}
      >
        {/* Close button */}
        <button
          ref={closeButtonRef}
          className={styles.closeButton}
          onClick={onClose}
          aria-label={lang === 'de' ? 'Menü schließen' : 'Close menu'}
          type="button"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Navigation links */}
        <nav aria-label={lang === 'de' ? 'Mobilnavigation' : 'Mobile navigation'}>
          <ul className={styles.navList}>
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`${styles.navLink} ${isActive(item.path) ? styles.navLinkActive : ''}`}
                  onClick={onClose}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Language switcher */}
        <div className={styles.languageSwitcherWrapper}>
          <LanguageSwitcher lang={lang} alternateSlug={alternateSlug} />
        </div>
      </div>
    </>,
    document.body,
  );
};

export default MobileMenu;
